// Manager's cheque — Stage 1 (Book deposit).
//
// Operator's flow: customer signs up + hands over the 2% deposit. Seller is
// engaged but not paid yet. Stage 2 (Pay seller) and Stage 3 (Deliver) happen
// hours/days later from the Open cheques card on Home.
//
// Layer A bucket guard + Layer B operator policy:
//   Customer pay-in rail: cash / angadiya / exchange / crypto        (no bank)
//   Desk pay-seller rail (Stage 2): cash / exchange / crypto          (also no bank)
//
// `crypto` joined both lists on 2026-08-22, when the wallet rails were renamed
// by chain and token (hot.trc.usdt -> crypto.hot-trc). Until then these read
// `hot` / `cold`, so the rename silently emptied the wallet section of this
// picker — the operator opened it to pay a 13,085 USDT deposit and saw only
// exchanges. `hot`/`cold` are kept in case a rail still carries those names.
//
// THE DEPOSIT IS A THREE-WAY GROUP when it lands in anything but AED. Operator,
// 12 Sept 2026: "I want screens everywhere for asset-1, asset-2 & rate and
// 3-way auto calculation, that too dynamic which changes with each number I
// change in the 2nd box" — and "even if I directly paste the rates, total
// should be there". Until then this screen had a lone rate box and did its own
// division; the coin figure could only be read, never corrected to what the
// customer actually sent. Now: deposit in AED, rate, deposit in the coin —
// any two fill the third, from the shared calculator (hooks/useTradeMath).
//
// 14 Sept 2026, the minimalist pass (SWEEP-SCOPE §5.3, §6, §7, item 43): one
// ScreenHeader with "About this screen" in place of the two-casing h1; the
// three fee boxes UNFOLDED — changed on 4 of 4 real cheques — and taken in
// PERCENT with "= N bps" beside, on all three stages, so "0.8" can no longer
// round to 1 bp (the payload still carries whole basis points); Notes under
// More; the FX and fee trios stack on the phone; the seller's empty line no
// longer contradicts the create-as panel; the success block is gone (the
// screen navigates away at once); the summary card is the shared KeyValue.
//
// 19 Sept 2026, the phone-layout pass: below lg the two deposits sit side by
// side with the Rate across beneath (Convert's shape), the two fees share a
// line under the deposit share, and a summary figure never breaks from its
// code. Nothing moves at lg+.
//
// 21 Oct 2026 audit (operator: "I need clean view for operator"): the deposit
// is said once besides the boxes — the summary card, now five rows (the
// "Cheque" row repeated Face amount; a "Customer total" row carries what the
// deleted fact line said, Astra 21 Oct) with one short hint each; the fact line
// that said it a third time is gone; the subtitle is "Stage 1 of 3 · deposit";
// the prefill percent and its chips stand on their own line above the two fees.
// What posts, what enables the button and every refusal are as they were.
//
// 22 Oct 2026 design-system sweep: the two party searches and the accounts read each say when they FAILED (a failed search read
// "No party by that name — type a new name to create them", an invitation to a duplicate); "paid in full" on a COIN deposit is
// made at the booking rate and is not final, so it is one neutral line with the figures behind Details (section 7: not fully
// checked is never green) while on an AED rail it is final and green; an over-paid deposit is one amber line; the card's coin
// figures wrap only between their "amount CODE" pieces, so nothing runs past the card at 360px.

import { useState, useMemo, useEffect, useRef } from 'react';
import { ApiError, isSentinel } from '../lib/errors';
import { RepeatPanel, useRepeatGuard } from '../components/RepeatPanel';
import { useQuery, useMutation } from '@tanstack/react-query';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { api } from '../lib/api';
import { tg } from '../lib/telegram';
import { Combobox, type ComboboxOption, NO_PARTY_CREATE_HINT } from '../components/Combobox';
import { rosterCut, usePartySearch } from '../lib/roster-cut';
import { RailPicker, type RailValue } from '../components/RailPicker';
import { MoreDetails } from '../components/MoreDetails';
import { ScreenHeader } from '../components/ScreenHeader';
import { Field, FieldRow } from '../components/Field';
import { Chip } from '../components/FilterRail';
import { ErrorLine } from '../components/ErrorLine';
import { ReadFailed, ServerLine, StateLine } from '../components/kit';
import { KeyValue, type KeyValueItem } from '../components/KeyValue';
import { fmtAmount, displayAsset } from '../lib/format';
import { partyName, partySubtitle, isStale, needsNameReview, rankForQuery, foldAsset, sameAsset } from '../lib/naming';
import { DEFAULT_RAILS, newChequeId, isFiat } from '../lib/workflow';
import { useTradeMath } from '../hooks/useTradeMath';
import { fmtQty } from '../lib/trade-math';
import { depositFacts, pctWords, type DepositFacts } from '../lib/mc-deposit';

/** Where a manager's-cheque leg may sit. No bank.*, no legacy.* — the operator's
 *  rule is about the KIND of money, not about what a rail happens to be called. */
const MC_PAY_IN_KINDS     = ['cash', 'angadiya', 'exchange', 'crypto', 'hot', 'cold'];
export const MC_PAY_OUT_KINDS = MC_PAY_IN_KINDS.filter((k) => k !== 'angadiya');

/** The seeds a deposit is usually asked at. "Full" is the whole cheque —
 *  face + commission — the case the screen could not name until 30 Sept 2026. */
const PREFILL_CHIPS = [['2', '2%'], ['10', '10%'], ['25', '25%'], ['50', '50%'], ['100', 'Full']] as const;

// Defaults, in PERCENT (§6 cheque fee unit). The seller's is the last one
// used on this device, else 0.40 — the desk changed it on every real cheque
// (40/45/45/40 against the old 50).
const DEPOSIT_PCT_DEFAULT      = '2';
const CUSTOMER_FEE_PCT_DEFAULT = '0.80';
const SELLER_FEE_PCT_DEFAULT   = '0.40';
const SELLER_FEE_PCT_KEY       = 'mc.sellerFeePct';   // per-device convenience only; the deal carries the figure

/** A cheque fee in percent with "= N bps" beside it — the one unit on all
 *  three stages (§6). The bps are what the deal is charged at (whole basis
 *  points), so they are shown as well as the typed percent. Used by the deal
 *  page's stage 2 and 3 forms too, so the box reads the same everywhere. */
export function FeePctField({ label, value, onChange, disabled, className }: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  disabled?: boolean;
  /** For the field's wrapper — the deal page caps its width at the booking
   *  screen's column (21 Oct 2026 audit: the box ran 880px wide for "0.40"). */
  className?: string;
}) {
  const bps = Math.round((Number(value) || 0) * 100);
  return (
    <Field label={label} className={className}>
      {/* The bps readout is a step narrower below lg: two fee boxes share one
          line on the phone (19 Sept 2026), and at 96px the readout was wider
          than the box being typed in. It stays the body size there — the bps
          are the figure the deal is charged at, not a hint — and "= 125 bps"
          fits the 80px box. At lg+ it is as it was. */}
      <div className="flex gap-2">
        <input className="input flex-1 min-w-0" value={value} onChange={(e) => onChange(e.target.value)} inputMode="decimal" disabled={disabled} aria-label={label} />
        {/* px-1, not the box's 10px: "= 125 bps" at the body size is 66px and the readout's content is 58px (80 − 2 × 10 − the
            border), so it ran to the border on a phone (22 Oct 2026 sweep). The phone keeps its 80px — two fee boxes share a line. */}
        <div className="w-20 lg:w-24 shrink-0 input px-1 bg-tg-bg/30 text-tg-hint text-center tabular text-body whitespace-nowrap">= {bps} bps</div>
      </div>
    </Field>
  );
}

/** The seller fee last used on this device, when there is one. */
function lastSellerFeePct(): string {
  try {
    const v = localStorage.getItem(SELLER_FEE_PCT_KEY);
    if (v && Number(v) > 0) return v;
  } catch { /* no storage — the default stands */ }
  return SELLER_FEE_PCT_DEFAULT;
}

/** What the summary card is read from — the booking screen's own figures,
 *  gathered so the card can be rendered (and pinned by a test) without the
 *  screen's typed state. Nothing here is computed again: each field is the
 *  screen's figure of the same name. */
export interface ChequeCardFacts {
  /** What the customer owes in all: face + their commission, in AED. */
  customerOwes: number;
  /** The deposit rail is not AED, so the card says the coins and the AED. */
  depositIsFx: boolean;
  /** The "fill any two" refusal, while the coin group is incomplete. */
  fxError: string | null;
  /** The deposit rail's asset. */
  asset: string;
  /** "AED per USDT·TRC" — the pair's way, from the calculator. */
  rateWords: string;
  fxRateN: number;
  /** The computed deposit in AED (what an AED rail books). */
  depositAmt: number;
  /** The AED deposit actually in the box, and its coin figure. */
  depositAedN: number;
  depositInRail: number;
  dep: DepositFacts;
  /** The prefill percent, used only while the share is not yet known. */
  depPctN: number;
  sellerPaysLater: number;
  balanceLater: number;
  balanceInRail: number;
  spread: number;
}

/** THE SUMMARY CARD — the one place the customer's total, the deposit, its
 *  share, the seller's amount, the balance and the spread are said (21 Oct 2026
 *  audit: the deposit was said in the boxes, in a fact line above the
 *  percentages and in this card; the "Cheque" row repeated Face amount; the
 *  hints wrapped to four lines in the 110px key column). Five rows; each hint
 *  is one short qualifier — "face + commission" on the total, the share of it on
 *  the deposit, the stage, and on a coin deposit "rate set at collection", which
 *  keeps the Balance an estimate. The rest of what the hints said is in About
 *  this screen. The TOTAL is its own row (Astra, 21 Oct): the fact line that
 *  carried "2,391,256.22 face + commission" is gone, and a total the operator
 *  has to add up from Deposit and Balance before booking is a money fact lost.
 *
 *  A figure and its code are one piece (19 Sept 2026, the phone-layout pass):
 *  in the phone's ~200px value column the deposit line wraps, and it used to
 *  break between "48,000.00" and "AED". Each "amount CODE" and the "@ rate
 *  words" are unbreakable, so the line wraps only between them. On a desk the
 *  line fits whole, as before. */
export function chequeSummaryRows(c: ChequeCardFacts): KeyValueItem[] {
  const piece = (words: string) => <span className="whitespace-nowrap">{words}</span>;
  // 22 Oct 2026 sweep (measured at 360px, where the value column is 178px): the unbreakable pieces are "amount CODE", "@ rate"
  // and "AED per USDT·TRC" — never a whole "@ 3.665 AED per USDT·TRC", which is 181px and spilled 3px into the card's padding.
  // At 390px the three still stand on one line (208px); at 360px the break falls between the two halves, never inside one and
  // never leaving "USDT·TRC" alone; and "≈" is outside its piece, free to end the line before it.
  const rate = <>{piece(`@ ${c.fxRateN}`)} {piece(c.rateWords)}</>;
  return [
    { label: 'Customer total', hint: 'face + commission', value: `${fmtAmount(c.customerOwes, 'AED')} AED` },
    {
      label: 'Deposit now',
      hint:  c.dep.known ? `${pctWords(c.dep.sharePct)}% of face + commission` : `prefill at ${c.depPctN}%`,
      value: c.depositIsFx
        ? (!c.fxError
            ? <>{piece(`${fmtAmount(c.depositInRail, c.asset)} ${displayAsset(c.asset)}`)} = {piece(`${fmtAmount(c.depositAedN, 'AED')} AED`)} {rate}</>
            : c.depositAedN > 0 ? `${fmtAmount(c.depositAedN, 'AED')} AED` : '')
        : `${fmtAmount(c.depositAmt, 'AED')} AED`,
    },
    { label: 'Seller', hint: 'stage 2', value: `${fmtAmount(c.sellerPaysLater, 'AED')} AED` },
    {
      label: 'Balance',
      hint:  `stage 3${c.depositIsFx ? ' · rate set at collection' : ''}`,
      value: c.depositIsFx && c.fxRateN > 0
        ? <>≈ {piece(`${fmtAmount(c.balanceInRail, c.asset)} ${displayAsset(c.asset)}`)} · {piece(`${fmtAmount(c.balanceLater, 'AED')} AED`)}</>
        : `${fmtAmount(c.balanceLater, 'AED')} AED`,
    },
    { label: 'Spread', hint: 'when settled', value: `${fmtAmount(c.spread, 'AED')} AED` },
  ];
}

export function ManagerCheque() {
  const nav = useNavigate();

  // Customer (lazy-create flow same as Book Trade)
  const [customerSlug, setCustomerSlug]   = useState('');
  const [pendingProfileChoice, setPending] = useState<null | { slug: string; display_name: string }>(null);
  // MC seller lazy-create — role picker shown when seller is new
  const [pendingSellerChoice, setPendingSeller] = useState<null | { slug: string; display_name: string }>(null);

  // Face + seller
  const [faceAmount, setFaceAmount]   = useState('');
  const [mcSellerSlug, setMcSeller]   = useState('');

  // ARRIVING FROM A CHAIN ROW (sweep item 31, 14 Sept 2026): ?rail= and
  // ?railAsset= name the wallet the deposit reached, ?take= the coins it
  // brought, ?note= the chain hash. The coin box is then LOCKED to the
  // chain's figure — the coins that arrived are the fact; the rate and the
  // AED are worked from them — so a cheque booked from the chain can never
  // disagree with it (a 70,000 USDT deposit that reached the books as
  // 69,995.41, sweep 3.1 #2). ?lock=coin is the same contract the Chain
  // screen hands the trade screen.
  const [search] = useSearchParams();
  const chainRail: RailValue | null = search.get('rail') && search.get('railAsset')
    ? { slug: search.get('rail')!, asset: search.get('railAsset')! } : null;
  const chainTake   = search.get('take') ?? '';
  // The lock belongs to the wallet the chain row named: move the pay-in rail
  // to another asset and the lock lets go (review, 14 Sept 2026 — a rail
  // switch used to leave a read-only, empty coin box behind).
  const chainLockable = !!chainRail && chainTake !== '' && Number(chainTake) > 0;

  // Customer pay-in rail (deposit lands here)
  const [customerRail, setCustomerRail] = useState<RailValue>(chainRail ?? { ...DEFAULT_RAILS.cash });
  const chainLocked = chainLockable && !!chainRail && sameAsset(customerRail.asset, chainRail.asset);

  // The three percentages, on screen from the start (§5.3 — changed on
  // every real cheque), typed in percent.
  const [depositPct, setDepositPct]         = useState(DEPOSIT_PCT_DEFAULT);
  const [customerFeePct, setCustomerFeePct] = useState(CUSTOMER_FEE_PCT_DEFAULT);
  const [sellerFeePct, setSellerFeePct]     = useState(lastSellerFeePct);
  const [notes, setNotes]                   = useState(search.get('note') ?? '');

  // THE DEPOSIT GROUP: deposit in AED, rate, deposit in the rail's asset. The
  // shared calculator owns all three (any two fill the third; amounts outrank
  // the rate; the rate reads the way the pair is quoted — "AED per USDT",
  // "INR per AED"). Required whenever the deposit rail isn't AED: the cheque
  // math is AED-denominated, and without a conversion the form used to relabel
  // AED amounts as USDT (2,000 AED deposit shown/booked as 2,000 USDT —
  // 2026-08-11 parallel-run finding). When the rail IS AED the group is not
  // shown and the hook sits idle.
  const math = useTradeMath(customerRail.asset, 'AED');
  // The AED box is prefilled with X% of face + commission (through preset(),
  // so it counts as the first amount on the screen and anchors when a rate is
  // pasted) and keeps following face/% until the operator types in that box
  // themselves — a figure they typed is the customer's, not ours to overwrite.
  // Blanking the box hands it back to the computed figure on the next change.
  const depositTyped = useRef(false);

  // ── data ────────────────────────────────────────────────────────────────
  const customers = usePartySearch({
    base: ['parties', 'customer'], text: customerSlug,
    ask: (q) => api.listParties({ entity: 'dubaiotc', type: 'customer', q }),
    enabled: customerSlug.length > 0,
  });
  const counterparties = usePartySearch({
    base: ['parties', 'counterparty'], text: mcSellerSlug,
    ask: (q) => api.listParties({ entity: 'dubaiotc', type: 'counterparty', q }),
    enabled: mcSellerSlug.length > 0,
  });
  const treasury = useQuery({
    queryKey: ['treasury', 'balances'],
    queryFn:  () => api.treasuryBalances('dubaiotc'),
  });

  const customerOptions: ComboboxOption[] = useMemo(() =>
    rankForQuery(
      (customers.data?.parties ?? []).filter((p) => p.party_type !== 'own_account'),
      customerSlug,
    )
      .map((p) => ({
        value:     p.slug,
        label:     partyName(p),
        secondary: partySubtitle(p, { compact: true }),
        stale:     isStale(p),
        review:    needsNameReview(p.display_name),
      }))
  , [customers.data, customerSlug]);
  // The typed text is a party only once it matches one (NewTrade's partyExists
  // idea); from then on the pay-in picker leads with that customer's own rails
  // (12 Sept 2026), never with a half-typed name's.
  const customerKnown = useMemo(
    () => (customers.data?.parties ?? []).some((p) => p.slug === customerSlug),
    [customers.data, customerSlug],
  );

  const sellerOptions: ComboboxOption[] = useMemo(() =>
    (counterparties.data?.parties ?? [])

      .map((p) => ({
        value:     p.slug,
        label:     partyName(p),
        secondary: partySubtitle(p, { compact: true }),
        stale:     isStale(p),
        review:    needsNameReview(p.display_name),
      }))
  , [counterparties.data]);

  // ── live math (all cheque amounts are AED-denominated) ─────────────────
  const face          = Number(faceAmount) || 0;
  // BASIS POINTS ARE WHOLE. `admin.deals.mc_customer_fee_bps` and
  // `mc_seller_fee_bps` are integer columns, and delivery truncates whatever it
  // is handed before charging the cheque, so a fraction would price the deal
  // on this screen at one rate and post it at another — the same gap stage
  // 3's fee box carried until 3 Sept. The box takes PERCENT (0.80) and shows
  // the bps it becomes (= 80 bps); the round happens once, here, where the
  // operator can still see the figure the deal is booked with.
  const custFeeBpsN   = Math.round((Number(customerFeePct) || 0) * 100);
  const selFeeBpsN    = Math.round((Number(sellerFeePct) || 0) * 100);
  const depPctN       = Number(depositPct) || 0;
  const customerFee   = face * custFeeBpsN / 10000;    // AED — commission ON the cheque value
  const sellerFee     = face * selFeeBpsN  / 10000;    // AED
  // ── WHAT THE CUSTOMER OWES ────────────────────────────────────────────────
  // The commission is ADDED ON the cheque value (operator, 2026-08-24: "+0.8%
  // should be added on the cheque value"), so the customer's obligation is
  // face + commission — and the booking deposit is a percentage of THAT, not of
  // the bare face.
  //
  // Until now this line read `face * depPctN / 100`, which quoted a deposit
  // 0.8% light: on a 2,372,278 face it asked for 12,981.00 USDT where the
  // operator had already collected 13,085. Booking the app's figure would have
  // left the wallet 103.85 USDT short of the coins that actually arrived, and
  // the chain leg would have carried the difference as an unexplained gap.
  //
  // The customer's TOTAL is unchanged either way (face + commission), and so is
  // the desk's spread — only the split between deposit and closing balance
  // moves, which is why the error was quiet.
  const customerOwes  = face + customerFee;            // AED
  const depositAmt    = customerOwes * depPctN / 100;  // AED — the computed figure
  const sellerPaysLater = face + sellerFee;
  const spread        = customerFee - sellerFee;

  // The deposit rail's asset is not AED: the three-way group is on screen and
  // every deposit figure below is read from it, never recomputed here.
  const depositIsFx   = customerRail.asset !== 'AED';
  const fxRateN       = Number(math.rate) || 0;
  // The AED deposit that will be booked: the group's AED box (prefilled with
  // the computed figure, possibly corrected by the operator) — or, on an AED
  // rail, the computed figure itself.
  const depositAedN   = depositIsFx ? (Number(math.amountOf('AED')) || 0) : depositAmt;
  const depositInRail = depositIsFx ? (Number(math.amountOf(customerRail.asset)) || 0) : depositAmt;
  // A corrected AED deposit changes what is left for stage 3.
  const balanceLater  = customerOwes - depositAedN;    // AED
  // The stage-3 balance is only an estimate at today's rate (it is set at
  // collection). It is a fourth number the group does not hold, so it reads
  // the rate the way the pair does: AED × "INR per AED", AED ÷ "AED per USDT".
  const aedIsBase     = foldAsset(math.base) === 'AED';
  const balanceInRail = depositIsFx
    ? (fxRateN > 0 ? (aedIsBase ? balanceLater * fxRateN : balanceLater / fxRateN) : 0)
    : balanceLater;
  // WHAT THE DEPOSIT IS (30 Sept 2026): its share of face + commission, what
  // it leaves for stage 3, whether it is the whole cheque — derived from the
  // AED actually put down, never read off the prefill box. The prefill is a
  // seed; this is the fact; every line below reads the fact. See
  // lib/mc-deposit.ts for why the two had to be separated.
  const dep = depositFacts(customerOwes, depositAedN);

  // 21 Oct 2026 audit: `depositKnown` is gone with the line it served. On 30
  // Sept 2026 (Mechanisms/Astra review) a deliberately typed "0" deposit had to
  // read as KNOWN — depositFacts() cannot tell a typed "0" from an untouched
  // box, and the fact line "Deposit X · N% of Y face + commission · leaves Z
  // for stage 3" showed nothing for it. That line said the deposit a third
  // time (the boxes, that line, the summary card) and is deleted; the card
  // carries the same figures once, and a zero deposit reads there as the
  // whole amount still to collect (Balance). The paid-in-full and paid-over
  // lines below stay: they say what the card does not.

  // Prefill: face × % lands in the AED box through preset() so it counts as
  // typed first (a pasted rate then moves the coin box, never this one). Runs
  // again whenever face, %, or the customer fee changes — until the operator
  // has typed in the AED box. A blank face blanks the box, and the calculator
  // blanks whatever it had derived from it.
  //
  // math.preset STAYS in the deps (12 Sept 2026, after preset() became
  // idempotent). It changes identity when the rail's asset changes, and the
  // re-run is then a no-op — the AED figure has already followed its asset to
  // its new box — with one exception worth keeping: an AED box the operator
  // blanked is filled again on a rail change, which is the "next change" the
  // note on depositTyped promises. Dropping the dep would leave that box empty
  // until face or % moved.
  //
  // NOT WHEN THE COINS CAME FROM THE CHAIN: the locked coin figure is the
  // first fact, and a prefilled AED would be the second — the rate would then
  // derive from face × % ÷ coins, which is nobody's rate. The operator types
  // the rate (or the AED actually agreed) and the other box fills.
  useEffect(() => {
    if (!depositIsFx || depositTyped.current || chainLocked) return;
    math.preset({ asset: 'AED' }, depositAmt > 0 ? fmtQty(depositAmt, 'AED') : '');
  }, [depositAmt, depositIsFx, math.preset, chainLocked]);
  // The chain's coins go in once, as if typed first (NewTrade's rule for
  // ?take=). Mount only: the URL does not change while the screen is open.
  useEffect(() => {
    if (chainLocked && chainRail) math.preset({ asset: chainRail.asset }, chainTake);
  }, []);   // eslint-disable-line react-hooks/exhaustive-deps

  const feeError = custFeeBpsN < selFeeBpsN ? 'Customer fee must be ≥ seller fee.' : null;
  const fxError = depositIsFx && !(depositAedN > 0 && fxRateN > 0 && depositInRail > 0)
    ? `Fill any two: Deposit in AED, Rate, Deposit in ${displayAsset(customerRail.asset)}.`
    : null;

  // ── submit ─────────────────────────────────────────────────────────────
  // ONE ID PER FORM FILL. The route dedupes on `${deal_id}:book`, but this
  // screen used to send no id, so the server minted a fresh one every attempt:
  // when a response was lost (tunnel drop, Worker timeout) the operator saw an
  // error, tapped again, and the customer carried TWO deposits under two deals
  // (3 Sept audit). A stable id makes the retry land on the same reference.
  const [dealId, setDealId] = useState(() => newChequeId());
  // ONE CHAIN TRANSFER, BOOKED ONCE (27 Oct 2026, Mechanisms' 9d9756c8): a cheque opened from a Chain row carries the
  // transfer's hash in its note and the wallet it landed on as the deposit's rail. When the books already hold that
  // transfer there, the route answers 409 repeat_suspected with the twin — the charge screen's repeat panel, here: go
  // back, or post it again anyway (confirm_repeat), whatever the press that met it also carried.
  const { twin: repeat, clearTwin, guard } = useRepeatGuard();
  const submit = useMutation({
    mutationFn: async (opts?: {
      profile_kind?: 'tracked' | 'treasury_only';
      mc_seller_role?: 'merchant' | 'other';
      confirm_repeat?: boolean;
    }) => {
      try {
        return await guard(() => api.bookManagerCheque({
          entity:                 'dubaiotc',
          deal_id:                dealId,
          customer_slug:          customerSlug,
          customer_display_name:  customerSlug,
          profile_kind:           opts?.profile_kind,
          mc_face_amount:         face,
          mc_seller_slug:         mcSellerSlug,
          mc_seller_display_name: mcSellerSlug,
          mc_seller_role:         opts?.mc_seller_role,
          customer_treasury_slug: customerRail.slug,
          customer_pay_asset:     customerRail.asset,
          // To the rail's own precision. Rounding to a WHOLE unit here quoted the
          // operator one figure and booked another — on a coin rail that left the
          // wallet short by up to a coin, and the chain leg carried the gap.
          customer_deposit_amount: Number(depositInRail.toFixed(isFiat(customerRail.asset) ? 2 : 6)),
          // THE SHARE PAID, not the prefill (30 Sept 2026): 44.38 for a
          // 1,624,013.27 deposit on a 3,659,613.55 cheque, whatever the seed
          // box says. The route derives the same figure from mc_deposit_aed
          // and stores THAT; this one serves a route that predates the field.
          mc_deposit_pct:         Number(dep.sharePct.toFixed(4)),
          // The deposit in AED and the rate that valued it, so the books hold
          // what the coins were worth on the day. Until now a USDT deposit was
          // booked with no AED figure and no rate, and its share could not be
          // recovered afterwards (MC-2026-09-30-1ULLM8 is one such).
          mc_deposit_aed:         Number(depositAedN.toFixed(2)),
          mc_deposit_rate_to_aed: depositIsFx && depositInRail > 0 ? Number((depositAedN / depositInRail).toFixed(8)) : undefined,
          mc_customer_fee_bps:    custFeeBpsN,
          mc_seller_fee_bps:      selFeeBpsN,
          // The note carries the three figures as they stood on screen — the
          // rate with its words, since "INR per AED" and "AED per USDT" read
          // opposite ways and a bare number would not say which.
          notes:                  depositIsFx
            ? [`Deposit FX: ${fmtAmount(depositAedN, 'AED')} AED @ ${fxRateN} ${math.rateWords} = ${fmtAmount(depositInRail, customerRail.asset)} ${displayAsset(customerRail.asset)}`, notes].filter(Boolean).join(' · ')
            : (notes || undefined),
          confirm_repeat:         opts?.confirm_repeat || undefined,
        }));
      } catch (e) {
        // The old try/catch{} here swallowed its own needs_confirm rethrow, so
        // a new party or seller surfaced as the raw API error (3 Sept audit).
        const body = e instanceof ApiError && e.status === 409
          ? (e.body as { needs_confirm?: string; suggested?: { slug: string; display_name: string } }) : null;
        if (body?.needs_confirm === 'create_party' && body.suggested) {
          setPending({ slug: body.suggested.slug, display_name: body.suggested.display_name });
          throw new Error('needs_confirm');
        }
        if (body?.needs_confirm === 'create_mc_seller' && body.suggested) {
          setPendingSeller({ slug: body.suggested.slug, display_name: body.suggested.display_name });
          throw new Error('needs_confirm');
        }
        throw e;
      }
    },
    onMutate: () => clearTwin(),
    onSuccess: (data) => {
      tg()?.HapticFeedback.notificationOccurred('success');
      setPending(null);
      setPendingSeller(null);
      setDealId(newChequeId());   // the next cheque is its own deal
      // The seller fee just used becomes the next cheque's default (§5.3).
      try { localStorage.setItem(SELLER_FEE_PCT_KEY, sellerFeePct); } catch { /* no storage — nothing to remember */ }
      // Single flow: after booking, land the operator on the deal's lifecycle screen
      if (data?.deal_id) nav(`/mc/${data.deal_id}`);
    },
    onError:   (e) => { if ((e as Error).message !== 'needs_confirm') tg()?.HapticFeedback.notificationOccurred('error'); },
  });

  const canSubmit = !submit.isPending && !!customerSlug && face > 0
                  && !!customerRail.slug && !!mcSellerSlug && !feeError && !fxError
                  && !pendingProfileChoice && !pendingSellerChoice && !repeat;

  const asset = customerRail.asset;
  // The summary card (stays, §5.3) — the shared KeyValue, without the two
  // separator rows; the stage rides under the label as its qualifier. Its
  // rows are built by chequeSummaryRows (above) from this screen's figures.
  const summary = chequeSummaryRows({
    customerOwes, depositIsFx, fxError, asset, rateWords: math.rateWords, fxRateN,
    depositAmt, depositAedN, depositInRail, dep, depPctN,
    sellerPaysLater, balanceLater, balanceInRail, spread,
  });

  return (
    <div className="space-y-3 lg:max-w-[720px] lg:mx-auto lg:space-y-4">
      <button onClick={() => nav(-1)} className="btn-link text-meta">← Back</button>
      {/* 21 Oct 2026 audit: the subtitle was an instruction ("deposit now;
          seller and delivery from Home → Open cheques", two lines on the phone).
          The stage is all it needs to say; the route to stages 2 and 3 is in
          About this screen, as it was. */}
      <ScreenHeader
        title="Manager's cheque"
        subtitle="Stage 1 of 3 · deposit"
        help={
          <>
            <p>
              The customer pays a deposit now and the cheque is booked; the seller is paid and the cheque
              handed over later, from the deal&rsquo;s own page (Home → Open cheques). The commission is added
              on the cheque value, so the deposit is a share of face + commission; what is left is taken at
              handover.
            </p>
            <p>
              The card under the fees: Customer total, face + commission; Deposit now with its share of it;
              Seller, paid at stage 2 (face + the seller&rsquo;s fee); Balance, collected at stage 3 — the commission added, less the
              deposit, and on a coin deposit only an estimate until the rate is set at collection; Spread,
              earned when the cheque settles.
            </p>
            <p>
              <strong className="font-medium text-tg-text">A deposit in coins.</strong> Any two of Deposit in
              AED, Rate and Deposit in the coin fill the third; Deposit in AED follows the deposit % until you
              type it yourself. Opened from a Chain row, the coins are what the chain brought and cannot be
              typed over — type the rate, or the AED agreed, and the other fills.
            </p>
            <p>
              Fees are in percent, two decimals, and the deal carries them as whole basis points — the box says
              which. The seller fee opens on the last one used.
            </p>
          </>
        }
      />

      <FieldRow>
        <Field label="Customer">
          <Combobox
            value={customerSlug}
            onChange={setCustomerSlug}
            options={customerOptions}
            more={rosterCut(customers.data)}
            placeholder="Search party"
            // a read in flight OR waiting for the network (a first read opened offline is paused): the list says "…", not "No party by that name"
            loading={customers.isFetching || customers.fetchStatus === 'paused'}
            emptyHint={customerSlug.length === 0 ? null : NO_PARTY_CREATE_HINT}
            // A SEARCH THAT FAILED IS NOT "NO PARTY BY THAT NAME" (22 Oct 2026 sweep): that line invites a new party to be
            // created — a duplicate of one the books hold. The failed line, with its Try again, is INSIDE the open list
            // (Combobox): below it, the list covers it and the control cannot be pressed (Astra, 22 Oct).
            readFailed={customers.isError ? <ReadFailed what="the parties" error={customers.error} onRetry={() => void customers.refetch()} /> : undefined}
          />
        </Field>
        <Field label="Face amount" hint="AED">
          <input className="input" value={faceAmount} onChange={(e) => setFaceAmount(e.target.value)} inputMode="decimal" aria-label="Face amount" />
        </Field>
      </FieldRow>

      <FieldRow>
        <Field label="Seller">
          <Combobox
            value={mcSellerSlug}
            onChange={setMcSeller}
            options={sellerOptions}
            more={rosterCut(counterparties.data)}
            placeholder="Search party"
            loading={counterparties.isFetching || counterparties.fetchStatus === 'paused'}
            emptyHint={mcSellerSlug.length === 0 ? null : NO_PARTY_CREATE_HINT}
            readFailed={counterparties.isError ? <ReadFailed what="the parties" error={counterparties.error} onRetry={() => void counterparties.refetch()} /> : undefined}
          />
        </Field>
        <Field label="Deposit lands in">
          <RailPicker
            value={customerRail}
            onChange={setCustomerRail}
            rows={treasury.data?.rows ?? []}
            // MC_PAY_IN_KINDS, not a literal: the same list is enforced server-side
            // in manager-cheque.ts::isCustomerPayRailAllowed, and the two drifting
            // apart is what hid every wallet from this picker after the rails were
            // renamed to crypto.* on 2026-08-22.
            allowedKinds={MC_PAY_IN_KINDS}
            partySlug={customerKnown ? customerSlug : undefined}
            loading={treasury.isFetching || treasury.fetchStatus === 'paused'}
            readFailed={treasury.isError}
            placeholder="Pay-in account"
          />
          {treasury.isError && <ReadFailed what="the accounts" error={treasury.error} onRetry={() => void treasury.refetch()} className="mt-1" />}
        </Field>
      </FieldRow>

      {depositIsFx && (
        <div className="space-y-1">
          {/* Three boxes, one row on desktop (§5.3). Values and setters come
              straight from the calculator; e.target.value is passed as typed
              so a rate pasted from Telegram lands untouched. THE BOX THAT
              MOVES SAYS SO (12 Sept 2026): the one the calculator fills
              carries "auto" as its hint and muted figures. The calculator
              decides (math.isDerived / math.rateIsDerived) — this screen
              never works it out itself.

              THE PHONE (19 Sept 2026, the phone-layout pass — Convert's
              shape): the two deposits are the related pair, so they sit side
              by side — AED, then the coin — and the Rate takes the whole
              line beneath them, where "AED per USDT · auto" has room. The
              Rate box stays second in the source (its lg+ seat and the tab
              order are as they were); below lg it is placed last. */}
          <FieldRow cols={3} className="max-lg:grid-cols-2">
            <Field label="Deposit in AED" hint={math.isDerived('AED') ? 'auto' : undefined}>
              <input
                className={`input${math.isDerived('AED') ? ' text-tg-hint' : ''}`}
                value={math.amountOf('AED')}
                onChange={(e) => {
                  // Typing here makes the AED figure the operator's own; a
                  // blank hands it back to face × % on the next change.
                  depositTyped.current = e.target.value.trim() !== '';
                  math.setAmount('AED', e.target.value);
                }}
                inputMode="decimal"
                aria-label="Deposit in AED"
              />
            </Field>
            <Field label="Rate" hint={math.rateIsDerived ? `${math.rateWords} · auto` : math.rateWords} className="max-lg:col-span-2 max-lg:order-last">
              <input
                className={`input${math.rateIsDerived ? ' text-tg-hint' : ''}`}
                value={math.rate}
                onChange={(e) => math.setRate(e.target.value)}
                inputMode="decimal"
                aria-label="Rate"
              />
            </Field>
            {/* "from chain" is not repeated here (22 Oct 2026 sweep): the prefill's hint says "coins came from the chain",
                and on a phone the hint wrapped this label onto two lines and put the coin box a row below its neighbour. */}
            <Field label={`Deposit in ${displayAsset(asset)}`}
              hint={!chainLocked && math.isDerived(asset) ? 'auto' : undefined}>
              {/* Locked to the chain's figure when the screen was opened from
                  a Chain row — read-only, so it cannot be typed over. */}
              <input
                className={`input${math.isDerived(asset) ? ' text-tg-hint' : ''}${chainLocked ? ' bg-tg-bg/30' : ''}`}
                value={math.amountOf(asset)}
                onChange={(e) => { if (!chainLocked) math.setAmount(asset, e.target.value); }}
                readOnly={chainLocked}
                aria-readonly={chainLocked || undefined}
                inputMode="decimal"
                aria-label={`Deposit in ${displayAsset(asset)}`}
              />
            </Field>
          </FieldRow>
          <ErrorLine className="mt-0">{fxError}</ErrorLine>
        </div>
      )}

      {/* WHAT THE DEPOSIT IS, said as a fact (30 Sept 2026). This replaced an
          amber line — "not 2% of face + commission — fine if that is what the
          customer paid" — that fired on the NORMAL case: a deposit is whatever
          the customer paid, and a warning that is usually wrong trains the eye
          past it, so it is not read on the day it is right. A fact cannot read
          wrong. Amber is kept for the one thing worth a second look: more was
          put down than the cheque costs. Shown on an AED rail too — "paid in
          full" is a state of the cheque, not of the rail. */}
      {/* ON A COIN RAIL EVERY ONE OF THESE FIGURES IS AT THE BOOKING RATE, AND
          SAYS SO (30 Sept 2026, an hour after the lines went in — CEX session's
          review). Stage 3 re-values the deposited coins at the rate typed at
          collection (manager-cheque.ts, deliver: rate_to_aed from THAT request),
          so "paid in full" made at 3.665 can leave a balance at 3.650. The
          not-paid-in-full path already called its balance "only an estimate";
          the paid-in-full path, from the same rate, stated a guarantee. 55 of
          60 cheques are FX — this is the normal case, not the edge. The
          qualifier goes when stage 3 consumes the booking valuation now kept
          in the :book metadata (deposit_aed, rate_to_aed).
          WHICH WAY IS "WORSE" DEPENDS ON HOW THE PAIR IS QUOTED (Bookkeeping's
          review, same day): "AED per USDT" leaves a balance when the number
          falls; "INR per AED" — the inverse — when it rises. aedIsBase already
          says which; the sentence reads it rather than assuming the direct
          quote for every coin. */}
      {/* 21 Oct 2026 audit: the not-paid-in-full FACT LINE that stood here —
          "Deposit 47,825.12 AED · 2% of 2,391,256.22 face + commission · leaves
          2,343,431.10 for stage 3 at the booking rate." — is deleted. It said
          the deposit a third time (the boxes above, then this, then the
          summary card below, which carries the same deposit, its share and the
          balance, and on a coin deposit "rate set at collection" on the
          Balance — the booking-rate qualifier the 30 Sept review asked for).
          The lines for a deposit that IS the whole cheque, or more, stay: they
          say what the card does not. */}
      {/* 22 Oct 2026 sweep: each line is ONE line, its figures behind Details. On a COIN rail "paid in full" is made at the
          BOOKING rate and stage 3 re-values the coins at the rate set at collection — it is not final, so it is not
          green (§7: something not fully checked is never green); on an AED rail it is final and says so. */}
      {dep.paidInFull && !(dep.over > 0) && (
        depositIsFx ? (
          <StateLine
            more={<>{fmtAmount(depositAedN, 'AED', 2)} AED covers face + commission at {fxRateN} {math.rateWords}. Stage 3 re-values the coins at the rate set at collection; if that rate is {aedIsBase ? 'higher' : 'lower'}, a balance remains.</>}
          >
            Paid in full at the booking rate — balance may remain.
          </StateLine>
        ) : (
          <StateLine tone="good" more={<>{fmtAmount(depositAedN, 'AED', 2)} AED covers face + commission.</>}>
            Paid in full — nothing due at stage 3.
          </StateLine>
        )
      )}
      {dep.over > 0 && (
        <StateLine
          tone="warn"
          more={depositIsFx
            ? <>Paid in full at {fxRateN} {math.rateWords}, {fmtAmount(dep.over, 'AED', 2)} AED more than face + commission at that rate — the excess moves with the collection rate.</>
            : <>Paid in full, and {fmtAmount(dep.over, 'AED', 2)} AED more than face + commission — the excess stays on the customer&rsquo;s box as theirs.</>}
        >
          {fmtAmount(dep.over, 'AED', 2)} AED over face + commission — the excess is the customer&rsquo;s.
        </StateLine>
      )}

      {/* The three percentages, unfolded (§5.3): changed on 4 of 4 real
          cheques. Percent in, bps beside.

          THE PHONE (19 Sept 2026): the two fees are the related pair — the
          guard beneath compares them — so they share one line; the deposit
          share takes the line above on its own. Three across would leave a
          fee box ~5px beside its bps readout at 375px. */}
      {/* THE PREFILL STANDS ON ITS OWN LINE (21 Oct 2026 audit): the box and its
          five chips shared a third of the fee row, so the chips wrapped under
          the box and broke the row; the hint "seeds the AED box" restated the
          label (the rule is in About this screen). The two fees keep the row
          beneath, a column each as before. The hint that stays says WHY the
          box is inert when the coins came from a chain row. */}
      <Field label="Prefill deposit at %" hint={chainLocked ? 'coins came from the chain' : undefined}>
        {/* THE SEED, NAMED AS ONE (30 Sept 2026). This box was "Deposit %",
            and stayed on screen — and was stored — as a description of the
            deposit after the operator had typed the real figure over its
            prefill. The description now lives in the summary; this only seeds
            the AED box. A chip is a
            deliberate re-seed, so it hands the AED box back to the prefill
            even after a figure was typed — the opposite of typing here,
            which never does. Inert when the coins came from a chain row:
            the locked figure is the first fact, and a prefill would derive
            a rate that is nobody's. */}
        <div className="flex flex-wrap items-center gap-1.5">
          <input className="input w-20 shrink-0" value={depositPct} onChange={(e) => setDepositPct(e.target.value)} inputMode="decimal" disabled={chainLocked} aria-label="Prefill deposit percent" />
          {PREFILL_CHIPS.map(([v, words]) => (
            <Chip key={v} active={depositPct === v} onClick={() => { if (chainLocked) return; depositTyped.current = false; setDepositPct(v); }}>{words}</Chip>
          ))}
        </div>
      </Field>
      <div className="space-y-1">
        <FieldRow cols={3} className="max-lg:grid-cols-2">
          <FeePctField label="Customer fee %" value={customerFeePct} onChange={setCustomerFeePct} />
          <FeePctField label="Seller fee %" value={sellerFeePct} onChange={setSellerFeePct} />
        </FieldRow>
        <ErrorLine className="mt-0">{feeError}</ErrorLine>
      </div>

      {face > 0 && (
        <section className="card-tight">
          {/* max-lg:gap-x-2: at 360px the value column is 178px; with the label gap at 8px instead of 14 it is 184px, which holds
              "33,351,483.5654 USDT·TRC" (182px) — the longest unbreakable piece a 9-digit coin deposit makes (22 Oct 2026 sweep). */}
          <KeyValue rows={summary} className="max-lg:gap-x-2" />
        </section>
      )}

      <MoreDetails label="More">
        <Field label="Notes">
          <textarea className="input h-16" value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Anything to remember" />
        </Field>
      </MoreDetails>

      {/* Every answer opens above the button (§7 rule 11). Creating a party
          or a seller IS the re-POST of the cheque, so the buttons lock while
          one is in flight (Book trade's one-tap rule). */}
      {pendingProfileChoice && (
        <div className="border border-tg-accent/40 rounded p-3 space-y-2 bg-tg-section-bg">
          <p className="text-body">
            <span className="font-semibold">{pendingProfileChoice.display_name}</span> doesn&rsquo;t exist yet — create as:
          </p>
          <div className="grid grid-cols-2 gap-2">
            <button type="button" className="btn-secondary text-body py-2 text-left px-3 disabled:opacity-40" disabled={submit.isPending} onClick={() => submit.mutate({ profile_kind: 'tracked' })}>
              <span className="font-medium">{submit.isPending && submit.variables?.profile_kind === 'tracked' ? 'Booking…' : 'Tracked'}</span>
              <span className="block text-meta text-tg-hint">KYC required</span>
            </button>
            <button type="button" className="btn-secondary text-body py-2 text-left px-3 disabled:opacity-40" disabled={submit.isPending} onClick={() => submit.mutate({ profile_kind: 'treasury_only' })}>
              <span className="font-medium">{submit.isPending && submit.variables?.profile_kind === 'treasury_only' ? 'Booking…' : 'Treasury-only'}</span>
              <span className="block text-meta text-tg-hint">No KYC; name only</span>
            </button>
          </div>
          <button type="button" className="btn-link text-meta text-tg-hint disabled:opacity-40" disabled={submit.isPending} onClick={() => setPending(null)}>Cancel</button>
        </div>
      )}

      {pendingSellerChoice && (
        <div className="border border-tg-accent/40 rounded p-3 space-y-2 bg-tg-section-bg">
          <p className="text-body">
            Seller <span className="font-semibold">{pendingSellerChoice.display_name}</span> doesn&rsquo;t exist yet — create as:
          </p>
          <div className="grid grid-cols-2 gap-2">
            <button type="button" className="btn-secondary text-body py-2 disabled:opacity-40" disabled={submit.isPending} onClick={() => submit.mutate({ mc_seller_role: 'merchant' })}>
              {submit.isPending && submit.variables?.mc_seller_role === 'merchant' ? 'Booking…' : 'Merchant'}
            </button>
            <button type="button" className="btn-secondary text-body py-2 disabled:opacity-40" disabled={submit.isPending} onClick={() => submit.mutate({ mc_seller_role: 'other' })}>
              {submit.isPending && submit.variables?.mc_seller_role === 'other' ? 'Booking…' : 'Other'}
            </button>
          </div>
          <button type="button" className="btn-link text-meta text-tg-hint disabled:opacity-40" disabled={submit.isPending} onClick={() => setPendingSeller(null)}>Cancel</button>
        </div>
      )}

      {repeat && (
        <RepeatPanel
          twin={repeat}
          pending={submit.isPending}
          onAlreadyBooked={() => { clearTwin(); nav(-1); }}
          onPostAnyway={() => submit.mutate({ ...submit.variables, confirm_repeat: true })}
        />
      )}

      {submit.isError && !isSentinel(submit.error) && <ServerLine error={submit.error} />}

      {/* The success block is gone (§5.3): a booked cheque navigates to its
          own page at once, so the block rendered for one frame. */}

      {/* lg+: hairline separator + right-aligned, content-sized submit */}
      <div className="lg:flex lg:justify-end lg:border-t lg:border-stroke-soft lg:pt-5">
        <button
          className="btn-primary w-full lg:w-auto lg:px-6"
          disabled={!canSubmit}
          onClick={() => submit.mutate(undefined)}
        >
          {submit.isPending ? 'Booking…' : 'Book MC · take deposit'}
        </button>
      </div>
    </div>
  );
}
